import { useQuery } from '@tanstack/react-query';
import { Button } from './ui/Button';
import { Alert } from './ui/Alert';
import { SheetBody, SheetFooter } from './ui/Sheet';
import { AutomationTimeline } from './AutomationTimeline';
import { HtmlPreview } from './html-template/HtmlPreview';
import { apiFetch } from '../lib/api';
import { formatOffset } from '../lib/timeline';
import { LEGAL_BASIS_LABELS, TRIGGER_LABELS, purposeLabel } from '../lib/automationLabels';
import type { BuiltinPreviewResponse, BuiltInFlowKey } from '../types/api';

/** The trigger's own settings in words; only inactivity has one worth saying. */
function triggerDetail(preview: BuiltinPreviewResponse['builtin']): string | null {
  const days = preview.triggerConfig.days;
  if (preview.triggerType === 'inactivity' && typeof days === 'number') {
    return `Starts once a contact has not bought, opened or clicked anything for ${days} days.`;
  }
  return null;
}

/**
 * What a not-yet-installed built-in flow does (Timo, 27 Oct 2026): when it
 * starts, its rules, every step with its timing and the real mails with
 * sample values, so it can be understood before "Install". Read-only;
 * `onInstall` is the same install the list row does.
 */
export function BuiltinPreviewPane({
  builtinKey,
  onInstall,
  installing,
  installError,
  onClose,
}: {
  builtinKey: BuiltInFlowKey;
  onInstall: () => void;
  installing: boolean;
  installError: string | null;
  onClose: () => void;
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['automations', 'builtins', builtinKey, 'preview'],
    queryFn: () => apiFetch<BuiltinPreviewResponse>(`/automations/builtins/${builtinKey}/preview`),
  });

  if (isError) {
    return (
      <SheetBody>
        <Alert>The preview could not be loaded. Try again.</Alert>
      </SheetBody>
    );
  }
  if (isLoading || !data) {
    return (
      <SheetBody>
        <div className="text-muted text-sm">Loading preview…</div>
      </SheetBody>
    );
  }

  const { builtin, timeline, mails } = data;
  const detail = triggerDetail(builtin);
  const offsets = new Map(timeline.map((e) => [e.stepId, e.offsetSeconds]));

  return (
    <>
      <SheetBody>
        <section>
          <h2 className="text-sm font-semibold m-0 mb-1">When it starts</h2>
          <p className="text-sm m-0">{TRIGGER_LABELS[builtin.triggerType]}</p>
          {detail ? <p className="text-sm text-muted m-0 mt-1">{detail}</p> : null}
        </section>

        <section aria-labelledby="preview-rules-heading">
          <h2 id="preview-rules-heading" className="text-sm font-semibold m-0 mb-2">
            Rules
          </h2>
          <ul className="m-0 pl-5 text-sm flex flex-col gap-1">
            <li>
              {builtin.stopOnPurchase
                ? 'Stops if the contact completes a purchase along the way.'
                : 'Continues even if the contact makes a purchase along the way.'}
            </li>
            <li>
              {builtin.allowReentry
                ? 'The contact can enroll in the flow again after it has finished.'
                : 'The contact can only complete the flow once.'}
            </li>
            {builtin.allowedLegalBasis.map((basis) => (
              <li key={basis}>Sent to: {LEGAL_BASIS_LABELS[basis]}</li>
            ))}
          </ul>
        </section>

        <section aria-labelledby="preview-steps-heading">
          <h2 id="preview-steps-heading" className="text-sm font-semibold m-0 mb-3">
            Steps
          </h2>
          <AutomationTimeline entries={timeline} />
        </section>

        <section aria-labelledby="preview-mails-heading" className="flex flex-col gap-4">
          <h2 id="preview-mails-heading" className="text-sm font-semibold m-0">
            The mails
          </h2>
          {mails.map((mail, i) => (
            <div key={mail.stepId} className="flex flex-col gap-2">
              <div className="text-sm">
                <span className="text-xs text-muted font-mono block">
                  Mail {i + 1} · {formatOffset(offsets.get(mail.stepId) ?? 0)} ·{' '}
                  {purposeLabel(mail.purpose)}
                </span>
                <span className="font-medium">Subject: {mail.subject}</span>
              </div>
              {mail.previewHtml ? (
                <HtmlPreview
                  html={mail.previewHtml}
                  title={`Preview of ${mail.subject}`}
                  className="h-[520px]"
                />
              ) : (
                <p className="text-sm text-muted m-0">
                  No ready-made design. After installing, choose a template for this mail before the
                  flow can be turned on.
                </p>
              )}
            </div>
          ))}
          {mails.some((m) => m.previewHtml) ? (
            <p className="text-xs text-muted m-0">
              Names, links and discount codes are examples. Every contact gets their own.
            </p>
          ) : null}
        </section>
      </SheetBody>
      <SheetFooter>
        {installError ? <Alert className="w-full">{installError}</Alert> : null}
        <Button variant="secondary" className="flex-grow" onClick={onClose}>
          Not now
        </Button>
        <Button className="flex-grow" disabled={installing} onClick={onInstall}>
          {installing ? 'Installing…' : 'Install (stays off)'}
        </Button>
      </SheetFooter>
    </>
  );
}
